import { useSendTextMutation } from '@queries/whatsapp/messages';
import { createSignal, Show } from 'solid-js';

/** Normalise a typed phone number to the digits WhatsApp addresses by (drops
 * spaces, dashes, parens, and a leading +). */
function digitsOnly(input: string): string {
  return input.replace(/[^0-9]/g, '');
}

/**
 * Start a brand-new WhatsApp conversation by phone number. WhatsApp linked
 * devices don't expose an address book, so reaching someone you haven't
 * chatted with yet means sending the first message to their number; that then
 * shows up as a normal conversation. Sends via {@link useSendTextMutation} and
 * hands the resulting chat jid back so the inbox can open it.
 */
export function NewChatModal(props: {
  accountId: string | undefined;
  onClose: () => void;
  onStarted: (chatJid: string) => void;
}) {
  const sendText = useSendTextMutation();
  const [phone, setPhone] = createSignal('');
  const [text, setText] = createSignal('');
  const [error, setError] = createSignal<string | undefined>();

  const canSend = () =>
    !!props.accountId &&
    digitsOnly(phone()).length >= 6 &&
    text().trim().length > 0 &&
    !sendText.isPending;

  async function start() {
    const account = props.accountId;
    const to = digitsOnly(phone());
    const body = text().trim();
    if (!account || !to || !body) return;
    setError(undefined);
    try {
      await sendText.mutateAsync({ accountId: account, to, text: body });
      props.onStarted(`${to}@s.whatsapp.net`);
      props.onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={props.onClose}
    >
      <div
        class="w-[360px] rounded-xl bg-white p-6 shadow-xl dark:bg-neutral-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg font-semibold">New conversation</h2>
          <button
            class="text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200"
            onClick={props.onClose}
          >
            ✕
          </button>
        </div>

        <label class="mb-1 block text-xs font-medium text-neutral-500">
          Phone number (international, e.g. +8613800138000)
        </label>
        <input
          class="mb-3 w-full rounded border border-neutral-300 bg-transparent px-2 py-1.5 text-sm dark:border-neutral-700"
          placeholder="+8613800138000"
          inputmode="tel"
          value={phone()}
          onInput={(e) => setPhone(e.currentTarget.value)}
        />

        <label class="mb-1 block text-xs font-medium text-neutral-500">
          First message
        </label>
        <textarea
          rows={3}
          class="mb-3 w-full resize-none rounded border border-neutral-300 bg-transparent px-2 py-1.5 text-sm dark:border-neutral-700"
          placeholder="Type a message…"
          value={text()}
          onInput={(e) => setText(e.currentTarget.value)}
        />

        <Show when={error()}>
          <p class="mb-2 text-sm text-red-500">{error()}</p>
        </Show>

        <button
          class="w-full rounded bg-green-600 py-1.5 text-sm text-white disabled:opacity-50"
          disabled={!canSend()}
          onClick={() => void start()}
        >
          {sendText.isPending ? 'Sending…' : 'Send & open chat'}
        </button>
        <p class="mt-2 text-center text-xs text-neutral-400">
          The person must already be on WhatsApp.
        </p>
      </div>
    </div>
  );
}
